import { CreditCardPurchaseCommandService } from "@/lib/creditCardPurchaseCommandService";
import {
  FINANCIAL_DATA_CHANGED_EVENT,
  publishFinancialExperienceUpdate,
} from "@/lib/financialExperience";
import {
  syncCreditCardInvoices as syncCreditCardInvoicesFromEngine,
  type CreditCardAmountMode,
  type CreditCardInvoiceStatus,
  type CreditCardTransactionType,
} from "@/lib/creditCardEngine";

export type {
  CreditCardAmountMode,
  CreditCardInvoiceStatus,
  CreditCardTransactionType,
};

export type CreditCardPurchaseCard = {
  id: string;
  closing_day: number | string;
  due_day: number | string;
};

export type CreditCardPurchaseRow = {
  id: string;
  user_id: string;
  card_id: string;
  invoice_id: string | null;
  description: string;
  category: string;
  sub_category?: string | null;
  amount: number | string;
  purchase_date: string;
  installments: number | string;
  current_installment: number | string;
  invoice_month: string;
  status: "pending" | "paid" | "overdue" | "cancelled" | null;
  paid_at: string | null;
  paid_from_account_id: string | null;
  installment_group_id?: string | null;
  amount_input_mode?: CreditCardAmountMode | null;
  transaction_type?: CreditCardTransactionType | null;
  original_purchase_id?: string | null;
  refund_reason?: string | null;
  created_at?: string;
};

export type CreditCardInvoiceRow = {
  id: string;
  user_id: string;
  card_id: string;
  invoice_month: string;
  closing_date: string;
  due_date: string;
  total_amount: number | string;
  status: CreditCardInvoiceStatus;
  paid_at: string | null;
  paid_from_account_id: string | null;
  paid_amount?: number | string | null;
  paid_transaction_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

type SyncInvoicesOptions = {
  card: CreditCardPurchaseCard;
  userId: string;
  existingInvoices?: CreditCardInvoiceRow[];
  extraInvoiceMonths?: string[];
};

type CreatePurchaseOptions = {
  userId: string;
  card: CreditCardPurchaseCard;
  description: string;
  category: string;
  subcategory?: string;
  notes?: string | null;
  amount: number;
  purchaseDate: string;
  invoiceMonth?: string;
  mode: "single" | "installment" | "repeat";
  installments: number;
  installmentStart: number;
  amountMode: CreditCardAmountMode;
  transactionType?: CreditCardTransactionType;
  isRefund?: boolean;
  originalPurchaseId?: string | null;
  refundReason?: string | null;
  existingInvoices?: CreditCardInvoiceRow[];
};

export const CREDIT_CARD_PURCHASE_SAVED_EVENT = "metajuntos:credit-card-purchase-saved";
export { FINANCIAL_DATA_CHANGED_EVENT };

function publishCreditCardChange(cardId?: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CREDIT_CARD_PURCHASE_SAVED_EVENT, { detail: { cardId } }));
  publishFinancialExperienceUpdate({
    source: "cards",
    action: "workspace-updated",
    surfaces: ["cards", "dashboard", "financial", "invoice", "net-worth"],
  });
}

export async function syncCreditCardInvoices({
  card,
  userId,
  existingInvoices = [],
  extraInvoiceMonths = [],
}: SyncInvoicesOptions) {
  return (await syncCreditCardInvoicesFromEngine({
    card,
    userId,
    existingInvoices,
    extraInvoiceMonths,
  })) as CreditCardInvoiceRow[];
}

export async function createCreditCardPurchaseWithInvoices(input: CreatePurchaseOptions) {
  const transactionType = input.transactionType ?? (input.isRefund ? "refund" : "purchase");
  const result = await CreditCardPurchaseCommandService.create(input.userId, input.card.id, {
    description: input.description.trim(),
    category: input.category.trim(),
    subcategory: input.subcategory?.trim() || null,
    notes: input.notes?.trim() || null,
    amount: Math.abs(input.amount),
    purchaseDate: input.purchaseDate,
    invoiceMonth: input.invoiceMonth || null,
    mode: input.mode,
    installments: input.mode === "single" ? 1 : input.mode === "repeat" ? 12 : input.installments,
    installmentStart: input.mode === "installment" ? input.installmentStart : 1,
    amountMode: input.amountMode,
    transactionType,
    originalPurchaseId: transactionType === "refund" ? input.originalPurchaseId ?? null : null,
    refundReason: transactionType === "refund" ? input.refundReason?.trim() || null : null,
  });
  publishCreditCardChange(input.card.id);
  return result;
}

export async function repairCreditCardInvoices({
  userId, card, existingInvoices = [],
}: {
  userId: string;
  card: CreditCardPurchaseCard;
  existingInvoices?: CreditCardInvoiceRow[];
}) {
  // Sync validates historical integrity and refuses repair of legacy drift.
  const savedInvoices = await syncCreditCardInvoices({
    card, userId, existingInvoices,
    extraInvoiceMonths: existingInvoices.map(invoice => invoice.invoice_month),
  });
  publishCreditCardChange(card.id);
  return savedInvoices;
}
